import { useDesignMaxTheme } from "@/packages";
import { usePensionNameValue } from "@/shared/client";
import { FONT } from "@/styles";
import { Box, Center, chakra, Flex, Text } from "@chakra-ui/react";
import DOMPurify from "dompurify";
import { motion } from "framer-motion";
import { useInView } from "react-intersection-observer";
import { type AboutSectionProps } from "./aboutSection.type";

export const AboutSection = ({ backColor = "#FBF6F2", data }: AboutSectionProps) => {
    const pensionName = usePensionNameValue();
    const { MEDIAS } = useDesignMaxTheme();
    const { isLaptop, isMobile } = MEDIAS();
    const { ref, inView } = useInView({ threshold: isMobile ? 0.3 : 0.9, triggerOnce: true, delay: 0.3 });
    const { sector, imgList } = { ...data };
    const engFontSize = isLaptop ? 32 : 48;
    return (
        <Center ref={ref} w="100%" backgroundColor={backColor}>
            <Center position="relative" as="section" p={isMobile ? "56px 0" : "140px 0"}>
                <Flex
                    flexDir={isMobile ? "column" : "row"}
                    gap={isMobile ? "10px" : "24px"}
                    p={isMobile ? "0 16px" : "0 48px"}
                >
                    <Flex flex="1" flexDir="column" justifyContent="space-between">
                        <MotionTextBox
                            initial={{ opacity: 0, y: 30 }}
                            animate={
                                inView
                                    ? { opacity: 1, y: 0, transition: { duration: 1, ease: "easeOut" } }
                                    : { opacity: 0, y: 0 }
                            }
                        >
                            <Box textAlign="left" mb={isMobile ? "16px" : "24px"}>
                                <Text {...FONT("notoSerif", isMobile ? 14 : 20, 500)}>{pensionName}</Text>
                                <Text {...FONT("cormorant", isMobile ? 24 : engFontSize, 700, 1.2)}>
                                    {sector.firstLine}
                                </Text>
                            </Box>
                            <Box textAlign="left">
                                <Text {...FONT("pretendard", isMobile ? 14 : 16, 300)}>{sector.secondLine}</Text>
                                <Text
                                    {...FONT("pretendard", isMobile ? 14 : 16, 300)}
                                    dangerouslySetInnerHTML={{
                                        __html: sector.thirdLine
                                            ? DOMPurify.sanitize(sector.thirdLine.replace(/\r?\n/g, "<br />"))
                                            : "",
                                    }}
                                />
                            </Box>
                        </MotionTextBox>
                        <MotionFirstImgBox
                            w={isMobile ? "100%" : "480px"}
                            h={isMobile ? "256px" : "360px"}
                            background={imgList[0] ? `url(${imgList[0].img_URL})no-repeat center/cover` : "gray.100"}
                            animate={{
                                borderTopRightRadius: inView ? "88px" : "0px",
                                transition: { duration: 1, ease: "easeInOut" },
                            }}
                        />
                    </Flex>
                    <Flex position="relative" justifyContent="flex-end" flex="1" h={isMobile ? "256px" : "640px"}>
                        <MotionTransImgBox
                            position="relative"
                            overflow="hidden"
                            maxW="680px"
                            w="100%"
                            h="100%"
                            background={imgList[1] ? `url(${imgList[1].img_URL})no-repeat center/cover` : "gray.100"}
                            animate={{
                                borderTopLeftRadius: inView ? "88px" : "0px",
                                transition: { duration: 1, ease: "easeInOut" },
                            }}
                        ></MotionTransImgBox>
                    </Flex>
                    <Box
                        display={isMobile ? "none" : "block"}
                        position="absolute"
                        left="50%"
                        bottom="27%"
                        w="356px"
                        h="125px"
                        transform="translate(-50%, 0) rotate(-90deg)"
                    >
                        <Text {...FONT("cormorant", 104, 700, 1.2)} color="#fff">
                            ABOUT
                        </Text>
                    </Box>
                </Flex>
            </Center>
        </Center>
    );
};

const MotionTextBox = chakra(motion.div);
const MotionFirstImgBox = chakra(motion.div);
const MotionTransImgBox = chakra(motion.div);
